/**
 * Stable API error codes the renderer matches on. The API echoes
 * these in the JSON body next to `message`; the renderer uses them
 * to switch UX without parsing user-facing copy.
 *
 * Keep this list narrow on purpose — adding a code here means we're
 * committing to a UX branch that handles it. Codes the renderer
 * doesn't care about stay as opaque error messages.
 */
export const AUTH_ERROR_CODES = {
  /** Slice 1+2 WorkOS Organization-membership gate. The user
   *  authenticated successfully with WorkOS but is not an active
   *  member of the configured Fluxter organization. */
  organizationMembershipRequired: 'api.auth.organization_membership_required',
} as const;

interface ErrorWithCode {
  error_code?: unknown;
}

/**
 * Narrow an unknown thrown value to "looks like an API error response
 * with this `error_code`". The shared API client throws the response
 * body verbatim on non-2xx, so we just check the shape.
 */
export const hasErrorCode = (err: unknown, code: string): boolean => {
  if (typeof err !== 'object' || err === null) return false;
  return (err as ErrorWithCode).error_code === code;
};

/** Convenience: did the API reject this sign-in because the user
 *  isn't an active member of the Fluxter WorkOS organization? */
export const isOrganizationMembershipRequired = (err: unknown): boolean =>
  hasErrorCode(err, AUTH_ERROR_CODES.organizationMembershipRequired);

/** Access-gate rejection — currently equivalent to
 *  `isOrganizationMembershipRequired`, kept as a separate name so
 *  callers don't have to change if we ever add another gate code. */
export const isAccessGateBlocked = (err: unknown): boolean =>
  isOrganizationMembershipRequired(err);
